import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import type { ChangeEvent, CSSProperties, KeyboardEvent as ReactKeyboardEvent, RefObject } from 'react';
import { fontFaceDescriptors } from '@course-authoring/shared-types';
import { themeFontFamilyName, themeFontCss, loadAndRegisterFont } from '../../theme/customFontLoader';
import { extractFontMetadata } from '../../theme/extractFontMetadata';
import {
  BASE_FONT_OPTIONS,
  CUSTOM_FONT_SENTINEL,
  type FontOption,
} from '../../theme/fontOptions';
import { FONT_ALLOWED_EXTENSIONS } from './themeSettingsUtils';
import type { AccountThemeDto } from '../../api/accountThemeApi';

export interface ThemeFontSectionViewModel {
  fontOptions: FontOption[];
  selectedFontValue: string;
  fontError: string | null;
  fontInputRef: RefObject<HTMLInputElement | null>;
  fontInputId: string;
  previewFontStyle: CSSProperties;
  onFontSelectChange: (event: ChangeEvent<HTMLSelectElement>) => void;
  onFontInputChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onTriggerFontFilePicker: () => void;
  onFontUploadKeyDown: (event: ReactKeyboardEvent<HTMLDivElement>) => void;
}

export interface ThemeFontSettingsInternals {
  isFontProcessing: boolean;
  fontFamilyForPayload: string;
  customFontAssetIdForPayload: number | null;
  stagedFontFile: File | null;
  seedFontFromDto: (dto: AccountThemeDto | undefined, builtInFontFamily: string) => void;
  sectionViewModel: ThemeFontSectionViewModel;
}

const displayNameOf = (filename: string): string => filename.replace(/\.[^.]+$/, '');

export function useThemeFontSettings(
  scopeAccountId: number | null | undefined,
  disabled = false,
): ThemeFontSettingsInternals {
  const fontInputRef = useRef<HTMLInputElement | null>(null);
  const fontInputId = useId();
  const previewInstanceId = useId().replaceAll(':', '');
  const operationRef = useRef(0);
  const scopeRef = useRef(scopeAccountId);
  const previousScopeRef = useRef(scopeAccountId);
  scopeRef.current = scopeAccountId;
  if (previousScopeRef.current !== scopeAccountId) {
    previousScopeRef.current = scopeAccountId;
    operationRef.current += 1;
  }
  const disabledRef = useRef(disabled);
  disabledRef.current = disabled;
  const temporaryFamilyRef = useRef<string | null>(null);
  const [fontOptions, setFontOptions] = useState<FontOption[]>(BASE_FONT_OPTIONS);
  const [selectedFontValue, setSelectedFontValue] = useState<string>('source-sans-pro');
  const [fontError, setFontError] = useState<string | null>(null);
  // Asset id of the account's retained custom font. Kept while a built-in font or a
  // staged replacement is selected (NA-454), so the font survives a switch and stays
  // available if the replacement upload fails at save time.
  const [customFontAssetId, setCustomFontAssetId] = useState<number | null>(null);
  const [stagedFontFile, setStagedFontFile] = useState<File | null>(null);
  const [isFontProcessing, setIsFontProcessing] = useState(false);

  const unregisterTemporaryFont = useCallback(() => {
    const family = temporaryFamilyRef.current;
    if (!family || typeof FontFace === 'undefined' || typeof document === 'undefined' || !document.fonts) return;
    for (const face of document.fonts) {
      if (face.family === family) document.fonts.delete(face);
    }
    temporaryFamilyRef.current = null;
  }, []);

  useEffect(() => () => {
    operationRef.current += 1;
    unregisterTemporaryFont();
  }, [unregisterTemporaryFont]);

  useEffect(() => {
    unregisterTemporaryFont();
    setStagedFontFile(null);
    setIsFontProcessing(false);
  }, [scopeAccountId, unregisterTemporaryFont]);

  const seedFontFromDto = useCallback((dto: AccountThemeDto | undefined, builtInFontFamily: string) => {
    operationRef.current += 1;
    unregisterTemporaryFont();
    setStagedFontFile(null);
    setIsFontProcessing(false);
    setFontError(null);
    const matchingBuiltIn = BASE_FONT_OPTIONS.find((option) => option.family === builtInFontFamily)
      ?? BASE_FONT_OPTIONS[0]!;
    if (dto?.customFont) {
      const { id, url, filename, metadata } = dto.customFont;
      const familyName = themeFontFamilyName(id);
      const operation = operationRef.current;
      loadAndRegisterFont(familyName, `url(${url})`, fontFaceDescriptors(metadata)).catch(() => {
        if (operationRef.current === operation) setFontError('The stored custom font could not be loaded. Please re-upload it.');
      });
      const retainedValue = `uploaded-${id}`;
      setFontOptions([
        ...BASE_FONT_OPTIONS,
        { value: retainedValue, label: `${displayNameOf(filename)} (uploaded)`, family: themeFontCss(familyName) },
      ]);
      setSelectedFontValue(dto.fontFamily === CUSTOM_FONT_SENTINEL ? retainedValue : matchingBuiltIn.value);
      setCustomFontAssetId(id);
    } else {
      setFontOptions(BASE_FONT_OPTIONS);
      setSelectedFontValue(matchingBuiltIn.value);
      setCustomFontAssetId(null);
    }
  }, [unregisterTemporaryFont]);

  const processFontFile = useCallback(async (file: File) => {
    if (disabledRef.current) return;
    const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
    if (!FONT_ALLOWED_EXTENSIONS.has(ext)) {
      setFontError('Unsupported file type. Please upload a WOFF, WOFF2, TTF, or OTF font file.');
      return;
    }
    const operation = ++operationRef.current;
    const scopeAtStart = scopeAccountId;
    setIsFontProcessing(true);
    const family = `__theme_font_preview_${previewInstanceId}_${operation}__`;
    const value = `uploaded-local-${operation}`;
    const displayName = file.name.replace(/\.[^.]+$/, '');
    try {
      const buffer = await file.arrayBuffer();
      // Declare the font's weights up front so bold is not synthesized in the preview.
      const previewMetadata = await extractFontMetadata(buffer);
      await loadAndRegisterFont(family, buffer, fontFaceDescriptors(previewMetadata));
      if (operationRef.current !== operation || scopeRef.current !== scopeAtStart) {
        if (typeof document !== 'undefined' && document.fonts) {
          for (const face of document.fonts) if (face.family === family) document.fonts.delete(face);
        }
        return;
      }
      unregisterTemporaryFont();
      temporaryFamilyRef.current = family;
      setFontOptions((current) => [
        ...current.filter((option) => !option.value.startsWith('uploaded-local-')),
        { value, label: `${displayName} (uploaded)`, family: themeFontCss(family) },
      ]);
      setSelectedFontValue(value);
      setStagedFontFile(file);
      setFontError(null);
      setIsFontProcessing(false);
    } catch {
      if (operationRef.current !== operation || scopeRef.current !== scopeAtStart) return;
      setFontError('The font file could not be loaded. Please check the file and try again.');
      setIsFontProcessing(false);
    }
  }, [scopeAccountId, previewInstanceId, unregisterTemporaryFont]);

  const handleFontInputChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (file) void processFontFile(file);
  }, [processFontFile]);

  const triggerFontFilePicker = useCallback(() => {
    if (!disabledRef.current) fontInputRef.current?.click();
  }, []);
  const handleFontUploadKeyDown = useCallback((event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      triggerFontFilePicker();
    }
  }, [triggerFontFilePicker]);

  const handleFontSelectChange = useCallback((event: ChangeEvent<HTMLSelectElement>) => {
    if (disabledRef.current) return;
    const value = event.target.value;
    operationRef.current += 1;
    unregisterTemporaryFont();
    setSelectedFontValue(value);
    setIsFontProcessing(false);
    setFontError(null);
    if (!value.startsWith('uploaded-local-')) {
      setStagedFontFile(null);
      setFontOptions((current) => current.filter((option) => !option.value.startsWith('uploaded-local-')));
    }
    // The retained custom font id is kept when a built-in font is selected (NA-454).
  }, [unregisterTemporaryFont]);

  const previewFontFamily = useMemo(() => {
    const match = fontOptions.find((option) => option.value === selectedFontValue);
    return match?.family ?? BASE_FONT_OPTIONS[0]!.family;
  }, [fontOptions, selectedFontValue]);
  const previewFontStyle = useMemo(() => ({ fontFamily: previewFontFamily }), [previewFontFamily]);
  const isCustomFont = selectedFontValue.startsWith('uploaded-');
  const fontFamilyForPayload = isCustomFont
    ? CUSTOM_FONT_SENTINEL
    : (fontOptions.find((option) => option.value === selectedFontValue)?.family ?? BASE_FONT_OPTIONS[0]!.family);

  return {
    isFontProcessing,
    fontFamilyForPayload,
    customFontAssetIdForPayload: customFontAssetId,
    stagedFontFile,
    seedFontFromDto,
    sectionViewModel: {
      fontOptions,
      selectedFontValue,
      fontError,
      fontInputRef,
      fontInputId,
      previewFontStyle,
      onFontSelectChange: handleFontSelectChange,
      onFontInputChange: handleFontInputChange,
      onTriggerFontFilePicker: triggerFontFilePicker,
      onFontUploadKeyDown: handleFontUploadKeyDown,
    },
  };
}
